import { Injectable } from '@angular/core';
import { Storage } from '@ionic/storage-angular';

@Injectable({
  providedIn: 'root'
})
export class StorageService {
  private _storage: Storage | null = null;
  private isReady = false; 
  private readyPromise: Promise<void>; 


  constructor(private storage: Storage) {
    this.readyPromise = this.init(); // Call init and wait for it
  }

  async init() {
    try {
      this._storage = await this.storage.create();
      this.isReady = true;
    } catch (error) {
      console.error('Error initializing storage', error);
    }
  }


  /**
   * Waits until storage is fully initialized
   */
  async waitUntilReady(): Promise<void> {
    if (this.isReady) {
      return Promise.resolve();
    }
    return this.readyPromise;
  }

  /**
   * Set a key-value pair in storage
   * @param key 
   * @param value 
   */
  async set(key: string, value: any): Promise<void> {
    await this.waitUntilReady();
    await this._storage!.set(key, value);
  }

  /**
   * Get a value from storage by key
   * @param key 
   * @returns 
   */
  async get<T>(key: string): Promise<T | null> {
    await this.waitUntilReady();
    const value = await this._storage!.get(key);
    return value;
  }

  /**
   * Remove an item from storage by key
   * @param key 
   */
  async remove(key: string): Promise<void> {
    await this.waitUntilReady();
    await this._storage!.remove(key);
  }

  /**
   * Clear all stored key-value pairs
   */
  async clear(): Promise<void> {
    await this.waitUntilReady();
    await this._storage!.clear();
  }

  /**
   * Get all keys stored
   * @returns 
   */
  async keys(): Promise<string[]> {
    await this.waitUntilReady();
    const keys = await this._storage!.keys();
    return keys || [];
  }

  /**
   * Get total number of items stored
   * @returns 
   */
  async length(): Promise<number> {
    await this.waitUntilReady();
    const length = await this._storage!.length();
    return length || 0;
  }
}
